import type { UseQueryOptions } from "@tanstack/react-query"

export type ItemSelectionMode = "single" | "multiple"
export type WithRequiredKeys<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>
/**
 * For Select/Autocomplete components that use typeahead search pattern with tanstack-query integration,
 * like AsyncAutocomplete, this type is used to define the query key.
 *
 * `searchTerm` is the internal implementation leakage that consumers need to be aware of.
 */
export type TypeaheadQuerykey<
  ApiQueryKeyId extends string = string,
  QueryParams extends { searchTerm: string } = { searchTerm: string },
> = [ApiQueryKeyId, QueryParams]

export type TypeaheadQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData extends UiOption[] = UiOption[],
  TQueryKey extends TypeaheadQuerykey = TypeaheadQuerykey,
> = WithRequiredKeys<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, "queryKey">

/**
 * Common types for list selection family of components:
 * - Autocomplete
 * - AsyncAutocomplete
 * - Select
 * - AsyncSelect
 */

/**
 * Base type for options that Select/Autocomplete components render.
 *
 * Please never update this type without consulting the team first.
 *
 * It is imperative to keep these complex type relations stay as is to have great DX and inversion of control,
 * allowing the Autocomplete/Select to be infinitely customisable without ever needing to change component code.
 *
 */
export type BaseOption<TValue extends string | number = string | number> = {
  label: string
  subtext?: string
  value: TValue
  /**
   * shadcn components can misbehave when 2 items have same value
   * we use uniqueKey to avoid this
   */
  uniqueKey: string

  /**
   * This is used to identify the type of the option.
   *
   * Added for the possible use-case of having other types like 'title', 'parent-option', etc.
   */
  optionType: "item"
}

/**
 * Base type for options that Select/Autocomplete components render.
 *
 * Please never update this type without consulting the team first.
 *
 * It is imperative to keep these complex type relations stay as is to have great DX and inversion of control,
 * allowing the Autocomplete/Select to be infinitely customisable without ever needing to change component code.
 *
 */

export type TitleOption<TValue extends string | number = string | number> = {
  label: string
  subtext?: string
  /**
   * title options aren't selectable so don't necessarily need a value, but leaving it just in case
   */
  value?: TValue

  uniqueKey: string

  optionType: "title"
}

/**
 * Might have more types of options as design system use cases expand
 */
export type UiOption<TValue extends string | number = string> =
  | BaseOption<TValue>
  | TitleOption<TValue>

export type UiOptionRenderType = UiOption["optionType"]

/**
 * In most cases, just use createUiOption utility to create options.
 * In cases where that's not feasible, we can use this helper to add the types to our options list.
 */
export type UiOptionHelper<
  CustomOptionData extends Record<string, unknown> = Record<string, unknown>,
  TValue extends string | number = string,
  OptionType extends UiOptionRenderType = "item",
> = OptionType extends "item"
  ? BaseOption<TValue> & CustomOptionData
  : OptionType extends "title"
    ? TitleOption<TValue> & CustomOptionData
    : never

export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never

/**
 * string literals are easier and more ergonomic for tagging types than enums.
 *
 * Also: https://blog.logrocket.com/why-typescript-enums-suck/
 */
export const UiOptionTypes = {
  item: "item" as const,
  title: "title" as const,
}
